import { mediaUrl } from "./api";

/** Register the offline-caching service worker (no-op if unsupported). */
export function registerOfflineCache(): void {
  if (!("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      /* ignore registration failures (e.g. unsupported context) */
    });
  });
}

let warming = false;
/** The newest key set requested while a warm run was already in progress. */
let queuedKeys: string[] | null = null;

/**
 * Warm the offline cache for the given media keys and prune anything no longer
 * referenced. Cached items play instantly; uncached ones download in the
 * background (a few at a time) while already-downloaded slides keep playing.
 */
export async function warmMediaCache(mediaKeys: string[]): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  if (warming) {
    // Remember the newest request instead of dropping it — otherwise a config
    // change made during a long download run would never get pre-cached.
    queuedKeys = mediaKeys;
    return;
  }
  warming = true;
  try {
    let keys: string[] | null = mediaKeys;
    while (keys) {
      await warmOnce(keys);
      keys = queuedKeys;
      queuedKeys = null;
    }
  } finally {
    warming = false;
  }
}

async function warmOnce(mediaKeys: string[]): Promise<void> {
  const paths = mediaKeys.map((k) => mediaUrl(k));

  // Tell the service worker which media is current so it can prune the rest.
  const reg = await navigator.serviceWorker.ready.catch(() => null);
  (reg?.active ?? navigator.serviceWorker.controller)?.postMessage({
    type: "prune-media",
    keep: paths.map((p) => new URL(p, location.origin).pathname),
  });

  // Background-download in small batches so playback isn't starved. The body
  // must be read to completion: fetch() resolves on HEADERS, so without this
  // every video downloaded at once (defeating the batching) and the unread
  // clone buffered whole files in memory.
  const batchSize = 3;
  for (let i = 0; i < paths.length; i += batchSize) {
    const batch = paths.slice(i, i + batchSize);
    await Promise.all(batch.map((p) => fetchAndDiscard(p)));
  }
}

async function fetchAndDiscard(path: string): Promise<void> {
  try {
    const res = await fetch(path);
    const reader = res.body?.getReader();
    if (!reader) return;
    // Drain without retaining: the service worker's clone is what gets cached.
    for (;;) {
      const { done } = await reader.read();
      if (done) return;
    }
  } catch {
    /* offline / transient failure — the next warm pass retries */
  }
}
